import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaDownload, FaList, FaListAlt } from "react-icons/fa";
import {
  ConfirmDialog,
  SecondaryButton,
  formatCurrency,
} from "../UI/DesignStandards";
import { squadPickerLabel } from "../ScoringHub/ScoringToolbar/scoringFilterOptions";
import { lastNameSortKey } from "../../utility/bowlerName";
import {
  aggregateOwedByBowler,
  aggregateOwedBySquad,
  aggregatePlaceLines,
  csvFromRows,
  detailPrizeCsv,
  payoutKindLabel,
  summaryPrizeCsv,
} from "../../utility/brackets/payoutTable";

const segmentActive = "bg-brand-charcoal text-brand-cream";
const segmentIdle = "text-brand-muted hover:text-brand-charcoal";
const thClass =
  "px-2 sm:px-3 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs md:text-sm font-display font-bold tracking-wider uppercase text-brand-cream bg-brand-charcoal";
const tdClass = "px-2 sm:px-3 md:px-6 py-3 sm:py-4 whitespace-nowrap";
const cellClass = "text-[10px] sm:text-xs md:text-sm text-brand-charcoal";

const PayTable = ({ label, columns, children }) => (
  <div className="overflow-x-auto">
    <table
      className="min-w-full divide-y divide-brand-charcoal/10"
      role="table"
      aria-label={label}
    >
      <thead className="bg-brand-charcoal">
        <tr>
          {columns.map((column) => (
            <th key={column} scope="col" className={thClass}>
              {column}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-brand-charcoal/10">{children}</tbody>
    </table>
  </div>
);

const PaidCheckbox = ({ checked, indeterminate, onChange, label }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = Boolean(indeterminate);
  }, [indeterminate]);
  return (
    <label className="inline-flex items-center gap-2">
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        aria-label={label}
      />
    </label>
  );
};

const PlacesList = ({ rows, squads, showSquad }) => (
  <details className="group">
    <summary className="cursor-pointer text-brand-gold-ink hover:underline font-medium">
      View places
    </summary>
    <div className="mt-2 p-3 card-inset whitespace-normal">
      <div className="space-y-2">
        {aggregatePlaceLines(rows).map((line) => {
          const squad = (squads || []).find((item) => item.id === line.squadID);
          const treeLabel = line.treeNumber
            ? `Tree ${line.treeNumber}`
            : "Leftover";
          const placeLabel =
            line.count > 1
              ? `${line.count} × ${payoutKindLabel(line.kind)}`
              : payoutKindLabel(line.kind);
          return (
            <div
              key={`${line.kind}-${line.squadID || ""}-${line.treeNumber || "leftover"}-${line.ticket?.id || ""}`}
              className="flex items-center justify-between gap-3 text-[10px] sm:text-xs md:text-sm"
            >
              <div className="flex-1 min-w-0">
                <span className="font-medium">{treeLabel}</span>
                {showSquad && squad ? (
                  <span className="text-brand-muted ml-2">
                    {" "}
                    ({squadPickerLabel(squad)})
                  </span>
                ) : null}
              </div>
              <div className="text-right shrink-0">
                <span className="font-score font-medium text-brand-charcoal">
                  {placeLabel}
                </span>
                <span className="font-score text-brand-charcoal ml-2 font-medium">
                  {" "}
                  {formatCurrency(line.amount)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </details>
);

const PayBoard = ({
  rows,
  bowlers,
  squads = [],
  tournamentId,
  search = "",
  onMarkPaid,
  canMarkPaid = false,
}) => {
  const [dataView, setDataView] = useState("summary");
  const [confirmSquad, setConfirmSquad] = useState(null);
  const [working, setWorking] = useState(false);

  const nameFor = (ticket) =>
    (bowlers || []).find((bowler) => bowler.id === ticket?.bowlerID)?.name ||
    "Bowler";

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rows || [];
    return (rows || []).filter((row) =>
      nameFor(row.ticket).toLowerCase().includes(query)
    );
  }, [rows, bowlers, search]);

  const bowlerRows = useMemo(
    () => aggregateOwedByBowler(filteredRows, bowlers),
    [filteredRows, bowlers]
  );
  const squadRows = useMemo(
    () => aggregateOwedBySquad(filteredRows, squads),
    [filteredRows, squads]
  );
  const detailRows = useMemo(
    () =>
      [...filteredRows].sort((left, right) => {
        const byName = lastNameSortKey(nameFor(left.ticket)).localeCompare(
          lastNameSortKey(nameFor(right.ticket))
        );
        if (byName) return byName;
        const bySquad = String(left.squadID || "").localeCompare(
          String(right.squadID || "")
        );
        if (bySquad) return bySquad;
        return Number(left.treeNumber || 0) - Number(right.treeNumber || 0);
      }),
    [filteredRows, bowlers]
  );

  const markPaid = async (prizeRows, paid) => {
    if (!canMarkPaid) return;
    await onMarkPaid(prizeRows, paid);
  };

  const handleExport = () => {
    const squadLabel = (row) => {
      const squad = (squads || []).find((item) => item.id === row.squadID);
      return squad ? squadPickerLabel(squad) : "";
    };
    const table =
      dataView === "summary"
        ? summaryPrizeCsv(bowlerRows)
        : detailPrizeCsv(
            detailRows.map((row) => ({
              ...row,
              bowlerName: nameFor(row.ticket),
            })),
            squadLabel
          );
    const blob = new Blob([csvFromRows(table)], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `bracket-prizes-${dataView}-${tournamentId || "export"}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  if (!rows?.length) {
    return (
      <p className="text-sm text-brand-charcoal">
        Nothing to pay yet. Leftover refunds show after shuffle. 1st and 2nd
        show after Game 3.
      </p>
    );
  }

  const unpaid = filteredRows.filter((row) => !row.paidAt);
  const totalUnpaid = unpaid.reduce(
    (sum, row) => sum + Number(row.amount || 0),
    0
  );
  const showSquadColumn = squads.length > 1;
  const showSquadTotals = squads.length > 1 && squadRows.length > 0;
  const singleSquad = !showSquadTotals && squadRows.length === 1 ? squadRows[0] : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-center justify-start sm:justify-end">
        <div
          className="flex bg-brand-cream rounded-lg p-1 border border-brand-charcoal/10"
          role="group"
          aria-label="Data view"
        >
          <button
            type="button"
            onClick={() => setDataView("summary")}
            className={`px-3 py-2 rounded-lg text-sm font-display font-bold transition-colors ${
              dataView === "summary" ? segmentActive : segmentIdle
            }`}
            aria-pressed={dataView === "summary"}
          >
            <FaList className="inline mr-2" aria-hidden="true" />
            Summary
          </button>
          <button
            type="button"
            onClick={() => setDataView("detail")}
            className={`px-3 py-2 rounded-lg text-sm font-display font-bold transition-colors ${
              dataView === "detail" ? segmentActive : segmentIdle
            }`}
            aria-pressed={dataView === "detail"}
          >
            <FaListAlt className="inline mr-2" aria-hidden="true" />
            Detail
          </button>
        </div>
        <SecondaryButton
          type="button"
          onClick={handleExport}
          icon={FaDownload}
          className="!px-3 !py-2 text-sm"
          aria-label={`Export ${
            dataView === "summary" ? "summary" : "detailed"
          } data as CSV file`}
        >
          <span className="sm:hidden">Export CSV</span>
          <span className="hidden sm:inline">
            Export {dataView === "summary" ? "Summary" : "Detailed"} CSV
          </span>
        </SecondaryButton>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="text-sm font-display font-bold text-brand-charcoal">
          {unpaid.length
            ? `${formatCurrency(totalUnpaid)} still to pay`
            : "All marked paid"}
        </div>
        {canMarkPaid && singleSquad ? (
          <SecondaryButton
            type="button"
            className="!px-3 !py-1.5 text-sm"
            disabled={working}
            onClick={() =>
              setConfirmSquad({
                squad: singleSquad.squad,
                rows: singleSquad.rows,
                paid: singleSquad.unpaid > 0,
              })
            }
          >
            {singleSquad.unpaid > 0 ? "Mark squad paid" : "Undo squad paid"}
          </SecondaryButton>
        ) : null}
      </div>

      {showSquadTotals ? (
        <PayTable
          label="Totals by squad"
          columns={[
            "Squad",
            "Total",
            "Still to pay",
            "Paid",
            ...(canMarkPaid ? ["Mark paid"] : []),
          ]}
        >
          {squadRows.map((row, index) => (
            <tr
              key={row.squadId}
              className={index % 2 === 0 ? "bg-white" : "bg-brand-cream/60"}
            >
              <td className={tdClass}>
                <div className={`${cellClass} font-medium`}>
                  {squadPickerLabel(row.squad)}
                </div>
              </td>
              <td className={tdClass}>
                <div className={`${cellClass} font-score font-semibold`}>
                  {formatCurrency(row.total)}
                </div>
              </td>
              <td className={tdClass}>
                <div className={`${cellClass} font-score`}>
                  {formatCurrency(row.unpaid)}
                </div>
              </td>
              <td className={tdClass}>
                <div className={`${cellClass} font-score`}>
                  {formatCurrency(row.paid)}
                </div>
              </td>
              {canMarkPaid ? (
                <td className={tdClass}>
                  <button
                    type="button"
                    className="text-sm font-medium text-brand-gold-ink hover:underline"
                    disabled={working}
                    onClick={() =>
                      setConfirmSquad({
                        squad: row.squad,
                        rows: row.rows,
                        paid: row.unpaid > 0,
                      })
                    }
                  >
                    {row.unpaid > 0 ? "Mark paid" : "Undo paid"}
                  </button>
                </td>
              ) : null}
            </tr>
          ))}
          <tr className="bg-brand-cream/80">
            <td className={tdClass}>
              <div className={`${cellClass} font-display font-bold`}>
                All squads
              </div>
            </td>
            <td className={tdClass}>
              <div className={`${cellClass} font-score font-semibold`}>
                {formatCurrency(
                  squadRows.reduce((sum, row) => sum + row.total, 0)
                )}
              </div>
            </td>
            <td className={tdClass}>
              <div className={`${cellClass} font-score`}>
                {formatCurrency(
                  squadRows.reduce((sum, row) => sum + row.unpaid, 0)
                )}
              </div>
            </td>
            <td className={tdClass}>
              <div className={`${cellClass} font-score`}>
                {formatCurrency(
                  squadRows.reduce((sum, row) => sum + row.paid, 0)
                )}
              </div>
            </td>
            {canMarkPaid ? <td className={tdClass} /> : null}
          </tr>
        </PayTable>
      ) : null}

      {filteredRows.length === 0 ? (
        <p className="text-sm text-brand-charcoal">No bowlers match that search.</p>
      ) : dataView === "summary" ? (
        <PayTable
          label="Payouts by bowler"
          columns={[
            "Bowler",
            "Total",
            "Still to pay",
            "Places",
            ...(canMarkPaid ? ["Paid"] : []),
          ]}
        >
          {bowlerRows.map((row, index) => (
            <tr
              key={row.bowlerId}
              className={index % 2 === 0 ? "bg-white" : "bg-brand-cream/60"}
            >
              <td className={tdClass}>
                <div
                  className={`${cellClass} font-medium max-w-[160px] sm:max-w-none truncate`}
                  title={row.bowlerName}
                >
                  {row.bowlerName}
                </div>
              </td>
              <td className={tdClass}>
                <div className={`${cellClass} font-score font-semibold`}>
                  {formatCurrency(row.total)}
                </div>
              </td>
              <td className={tdClass}>
                <div className={`${cellClass} font-score`}>
                  {formatCurrency(row.unpaid)}
                </div>
              </td>
              <td className="px-2 sm:px-3 md:px-6 py-3 sm:py-4">
                <div className={cellClass}>
                  <PlacesList
                    rows={row.rows}
                    squads={squads}
                    showSquad={showSquadColumn}
                  />
                </div>
              </td>
              {canMarkPaid ? (
                <td className={tdClass}>
                  <PaidCheckbox
                    checked={row.unpaid === 0}
                    indeterminate={row.paid > 0 && row.unpaid > 0}
                    onChange={(paid) => markPaid(row.rows, paid)}
                    label={`Mark ${row.bowlerName} paid`}
                  />
                </td>
              ) : null}
            </tr>
          ))}
        </PayTable>
      ) : (
        <PayTable
          label="Payouts by tree"
          columns={[
            "Bowler",
            ...(showSquadColumn ? ["Squad"] : []),
            "Tree",
            "Place",
            "Amount",
            ...(canMarkPaid ? ["Paid"] : []),
          ]}
        >
          {detailRows.map((row, index) => {
            const squad = (squads || []).find((item) => item.id === row.squadID);
            return (
              <tr
                key={`${row.kind}-${row.ticket.id}`}
                className={index % 2 === 0 ? "bg-white" : "bg-brand-cream/60"}
              >
                <td className={tdClass}>
                  <div
                    className={`${cellClass} font-medium max-w-[160px] sm:max-w-none truncate`}
                    title={nameFor(row.ticket)}
                  >
                    {nameFor(row.ticket)}
                  </div>
                </td>
                {showSquadColumn ? (
                  <td className={tdClass}>
                    <div className={cellClass}>
                      {squad ? squadPickerLabel(squad) : "—"}
                    </div>
                  </td>
                ) : null}
                <td className={tdClass}>
                  <div className={cellClass}>
                    {row.treeNumber ? `Tree ${row.treeNumber}` : "Leftover"}
                  </div>
                </td>
                <td className={tdClass}>
                  <div className={cellClass}>
                    {payoutKindLabel(row.kind)}
                  </div>
                </td>
                <td className={tdClass}>
                  <div className={`${cellClass} font-score font-semibold`}>
                    {formatCurrency(row.amount)}
                  </div>
                </td>
                {canMarkPaid ? (
                  <td className={tdClass}>
                    <PaidCheckbox
                      checked={Boolean(row.paidAt)}
                      onChange={(paid) => markPaid([row], paid)}
                      label={`Mark ${nameFor(row.ticket)} ${payoutKindLabel(
                        row.kind
                      )} paid`}
                    />
                  </td>
                ) : null}
              </tr>
            );
          })}
        </PayTable>
      )}

      <ConfirmDialog
        isOpen={Boolean(confirmSquad)}
        onClose={() => setConfirmSquad(null)}
        onConfirm={async () => {
          if (!confirmSquad) return;
          setWorking(true);
          try {
            await onMarkPaid(confirmSquad.rows, confirmSquad.paid);
            setConfirmSquad(null);
          } finally {
            setWorking(false);
          }
        }}
        title={
          confirmSquad?.paid
            ? "Mark this squad paid?"
            : "Undo paid for this squad?"
        }
        message={
          confirmSquad
            ? `${squadPickerLabel(confirmSquad.squad)}. This updates every prize for that squad.`
            : ""
        }
        confirmText={confirmSquad?.paid ? "Mark paid" : "Undo paid"}
        loading={working}
      />
    </div>
  );
};

export default PayBoard;
